import {
  Box,
  Button,
  CardMedia,
  Dialog,
  Grid,
  IconButton,
  SelectChangeEvent,
  Typography,
} from '@mui/material';
import { TwoNotes, ProceedSaveLater, SubHeading, Notes } from './components';
import { groupDocumentsBySection } from './documentSections';
import { Formik, useField, validateYupSchema, yupToFormErrors } from 'formik';
import MFSelectField from '../../lib/formik/SelectField';
import React, { useEffect, useState } from 'react';
import {
  AMC_APPROVER_CHECK_FOR_INDIVIDUAL,
  applicantStatusMasters,
  APPLICATION_TYPE_FOR_DOCUMENTS,
  ENABLE_KRA_DOCUMENT_FETCH,
  fewFieldsInKYCDetailsAndInvestorSignatureDocumentValidationDate,
  IMAGE_UPLOAD_FORMATS,
  INVESTOR_IMAGE_UPLOAD_FORMATS,
  SAVE_LATER_VALIDATION_CHECK,
  USER_ROLES,
  DOCUMENT_ALLOW_PASTE,
} from '../../utils/constant';
import { useDispatch, useSelector } from 'react-redux';
import { RootStateType } from '../../redux-store/reducers';
import {
  Applicant,
  Bank,
  Document,
  FileType,
  individuals_Poa_nonIndividuals_Documents,
  KraDocumentType,
  NomineeType,
} from '../../redux-store/types/api-types';
import {
  getDocuments,
  isFormValidForSubmission,
  updateApplication,
} from '../../redux-store/actions/application';
import { useHistory, useLocation } from 'react-router';
import { FilePond, registerPlugin } from 'react-filepond';
import 'filepond/dist/filepond.min.css';
import FilePondPluginImageExifOrientation from 'filepond-plugin-image-exif-orientation';
import FilePondPluginImagePreview from 'filepond-plugin-image-preview';
import FilePondPluginFileValidateSize from 'filepond-plugin-file-validate-size';
import FilePondPluginFileValidateType from 'filepond-plugin-file-validate-type';
import 'filepond-plugin-image-preview/dist/filepond-plugin-image-preview.css';
import { documentDetailsSchema } from '../../utils/schema';
import { FileUploadDialog } from '../commonComponents';
import { logout, showError } from '../../redux-store/actions/auth';
import en from '../../lang/en-us';
import {
  checkValidationBasedOnDate,
  cleardpIdField,
  getApplicantName,
  getCurrentStep,
  getDocNumberForMultipleDocs,
  isMinor,
  navigateToNextStep,
} from '../../utils/utilityFunctions';
import { getFieldTitle } from '../ApplicationDetails/DocumentDetails';
import { applicationComparison, applyRoleBasedStatus } from '../../utils/utilityFunctions';
import { useSnackbar } from 'notistack';
import { getNationalityList } from '../../redux-store/actions';
import { nationaliyType } from '../../redux-store/types/mdms';
import { KraDocument } from './kraDocument';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CloseIcon from '@mui/icons-material/Close';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';

registerPlugin(
  FilePondPluginImageExifOrientation,
  FilePondPluginImagePreview,
  FilePondPluginFileValidateSize,
  FilePondPluginFileValidateType
);

const FileUpload = ({
  onFileChange,
  name,
  documentData,
  disabled,
  onFileUpload,
  onFileUploaded,
}: {
  onFileChange: (value: string, documentId: string, removeFile?: boolean) => void;
  name: string;
  documentData: documentDetails;
  disabled: boolean;
  onFileUpload: () => void;
  onFileUploaded: () => void;
}): JSX.Element => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [field, meta] = useField(name);
  const errorText = meta.error && meta.touched ? meta.error : '';
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [files, setFiles] = useState<any>([]);
  const authToken = useSelector((store: RootStateType) => store.auth.token);
  const dispatch = useDispatch();
  const [open, setOpen] = useState({ show: false, message: '', code: '' });

  const handleOpen = (result: any) => {
    setOpen({ show: true, message: result.message, code: result.code });
  };
  const handleClose = () => {
    setOpen({ show: false, message: '', code: '' });
  };

  const handleFileChange = (name: string, documentId: string) => {
    try {
      onFileChange(name, documentId);
    } catch (e) {
      onFileUploaded();
      console.error((e as Error).message);
    }
  };
  return (
    <>
      {!documentData.file ? (
        <Box
          sx={{
            '.filepond--credits': { display: 'none' },
            '.filepond--label-action': {
              textDecoration: 'none',
              fontSize: '14px',
            },
            '.filepond--drop-label': {
              cursor: disabled ? 'not-allowed' : 'pointer',
              pointerEvents: 'all',
            },
            '.filepond--file': {
              color: '#000',
              flexWrap: 'wrap',
              fontWeight: 500,
            },
            pt: 4,
          }}>
          <FilePond
            files={files}
            server={{
              process: {
                url: '/api/files/files',
                method: 'POST',
                headers: {
                  Authorization: `Bearer ${authToken}`,
                },
                onload: (response): string => {
                  const result = JSON.parse(response);
                  result?.name && result?.id && handleFileChange(result.name, result.id);
                  onFileUploaded();
                  return response;
                },
                onerror: (response) => {
                  if (response) {
                    const result = JSON.parse(response);
                    if (result && result?.message) {
                      handleOpen(result);
                    }
                  } else {
                    handleOpen({ code: '', message: en.networkText.unableToProcess });
                  }
                  return response;
                },
              },
            }}
            onaddfilestart={() => {
              onFileUpload();
            }}
            onaddfile={(error, file) => {
              if (error) {
                onFileUploaded();
              }
            }}
            onprocessfile={(error, file) => {
              if (error) {
                if (error.code === 401) {
                  dispatch(logout());
                } else {
                  onFileUploaded();
                }
              }
            }}
            onupdatefiles={(fileItems) => {
              setFiles(fileItems);
            }}
            onprocessfilerevert={() => {
              onFileChange('', '', true);
            }}
            name="file"
            maxFileSize={'2MB'}
            allowMultiple={false}
            maxFiles={1}
            acceptedFileTypes={
              ['investorSignature', 'investorPhotograph'].includes(documentData.documentType)
                ? INVESTOR_IMAGE_UPLOAD_FORMATS
                : IMAGE_UPLOAD_FORMATS
            }
            labelIdle={`<span class="filepond--label-action">Choose File <div>Max Size: 2MB</div>Supports: ${
              ['investorSignature', 'investorPhotograph'].includes(documentData.documentType)
                ? 'png,jpg,jpeg'
                : 'jpeg,pdf,png,jpg'
            }</span>`}
            labelFileTypeNotAllowed={'Invalid file type'}
            labelMaxFileSize={'Max file size is 2 MB'}
            fileValidateTypeLabelExpectedTypesMap={{
              'image/jpeg': '.jpeg',
              'image/jpg': '.jpg',
              'image/png': '.png',
              'application/pdf': '.pdf',
            }}
            imagePreviewHeight={100}
            disabled={disabled}
            allowPaste={DOCUMENT_ALLOW_PASTE}
          />
        </Box>
      ) : (
        <Box
          sx={{
            border: '1px dashed #AAB2BA',
            borderRadius: '6px',
            mt: '30px',
            p: 1.5,
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
          }}>
          <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Typography
              component="p"
              sx={{
                fontSize: 13,
                fontWeight: 500,
                color: 'text.secondary',
                wordBreak: 'break-word',
                overflowWrap: 'break-word',
                m: 0,
              }}>
              {documentData.file.name}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              <Button
                size="small"
                sx={{
                  color: 'primary.main',
                  border: '1px dashed #2057A6',
                  fontSize: 12,
                  fontWeight: 500,
                  px: 1.5,
                  py: 0.5,
                }}
                onClick={() => window.open(documentData.file?.viewUrl)}>
                View
              </Button>
              {!disabled && (
                <Button
                  size="small"
                  variant="contained"
                  sx={{ fontSize: 12, fontWeight: 500, px: 1.5, py: 0.5 }}
                  onClick={() => {
                    onFileChange('', '', true);
                  }}>
                  Replace
                </Button>
              )}
              <IconButton
                size="small"
                sx={{
                  ':hover': {
                    bgcolor: '#F4FCFC',
                  },
                  cursor: 'pointer',
                }}
                onClick={() => window.open(documentData.file?.url, '_blank')}>
                <CardMedia
                  component="img"
                  src={'/images/download-icon.png'}
                  alt="Download"
                  sx={{ width: '20px' }}
                />
              </IconButton>
            </Box>
          </Box>
          <CardMedia
            component="img"
            src={documentData.file.thumbnailUrl || '/images/photo-placeholder.jpg'}
            alt="uploaded document preview"
            sx={{
              maxWidth: { xs: '80px', sm: '120px', md: '150px' },
              maxHeight: { xs: '60px', sm: '80px', md: '100px' },
              width: 'auto',
              height: 'auto',
              objectFit: 'contain',
              borderRadius: '6px',
              flexShrink: 0,
              border: '1px solid #E8ECF0',
            }}
          />
        </Box>
      )}
      <FileUploadDialog
        handleClose={handleClose}
        show={open.show}
        message={open.message}
        code={open.code}
      />
      {!!errorText && (
        <Typography
          sx={{
            color: '#d32f2f',
            fontSize: '0.75rem',
          }}>
          {errorText}
        </Typography>
      )}
    </>
  );
};

type documentDetails = {
  documentType: string;
  documentName: string;
  documentId: string;
  isActive: boolean;
  file?: FileType;
  required: string;
  isVisible: boolean;
  uniqueKey: string;
  options?: string[];
  infoText?: string[];
  downloadTemplate?: string;
  [key: string]: string | boolean | FileType | undefined | string[];
};

type documentProps = {
  documents: {
    documentType: string;
    documentName: string;
    documentsList: documentDetails[];
    multipleFiles: string;
    required: string;
    options?: string[];
    infoText?: string[];
    downloadTemplate?: string;
  }[];
  kraDocument?: KraDocumentType[];
  occupationDetails: string;
};
type nomineeDocumentProps = {
  nomineedocuments: {
    documentType: string;
    documentName: string;
    documentsList: documentDetails[];
    multipleFiles: string;
    required: string;
    options?: string[];
  }[];
};

export interface Values {
  applicants: documentProps[];
  nominees: nomineeDocumentProps[];
  saveType: string;
  banks: Bank[];
  hasPOA: boolean;
  [key: string]:
    | documentProps[]
    | string
    | undefined
    | string[]
    | Bank[]
    | boolean
    | nomineeDocumentProps[];
}
export const document_object = {
  documentId: '',
  isActive: true,
};
export default function DocumentDetails(): JSX.Element {
  const [loading, setLoading] = useState(false);
  const { application } = useSelector((store: RootStateType) => store.application);
  const { role = '' } = useSelector((store: RootStateType) => store.auth);
  const dispatch = useDispatch();
  const history = useHistory();
  const [documentUpload, setDocumentUpload] = useState(false);
  const [popUpText, setPopUpText] = useState<{ popUpInfoText: string[] } | null>(null);
  const [open, setOpen] = useState(false);
  const handleUpload = () => {
    setDocumentUpload(true);
  };
  const handleUploaded = () => {
    setDocumentUpload(false);
  };
  const [documentsData, setDocumentsData] = useState<Document>();
  const { enqueueSnackbar } = useSnackbar();
  const location = useLocation();
  const path = location.pathname;

  const handleOpen = () => {
    setOpen(true);
  };
  const handleClose = () => {
    setOpen(false);
  };

  useEffect(() => {
    (async function () {
      try {
        const response = (await dispatch(getDocuments())) as unknown as Document;
        setDocumentsData(response as Document);
      } catch (e) {
        console.error((e as Error).message);
      }
    })();
  }, []);

  const { applicants = [], banks = [], hasPOA = true, nominees = [] } = application || {};

  const initialValues: Values = {
    applicants: applicants.map((applicant, applicant_index) => {
      let docData = '';
      if (
        !hasPOA &&
        applicantStatusMasters[applicant.status as string] === applicantStatusMasters.Individual
      ) {
        docData = APPLICATION_TYPE_FOR_DOCUMENTS.INDIVIDUAL;
      }
      if (
        hasPOA &&
        applicantStatusMasters[applicant.status as string] === applicantStatusMasters.Individual
      ) {
        docData = APPLICATION_TYPE_FOR_DOCUMENTS.INDIVIDUAL_POA;
      }
      if (
        !hasPOA &&
        applicantStatusMasters[applicant.status as string] === applicantStatusMasters.NRI
      ) {
        docData = APPLICATION_TYPE_FOR_DOCUMENTS.NRI;
      }
      if (
        hasPOA &&
        applicantStatusMasters[applicant.status as string] === applicantStatusMasters.NRI
      ) {
        docData = APPLICATION_TYPE_FOR_DOCUMENTS.NRI_POA;
      }
      return {
        documents:
          typeof documentsData !== 'undefined'
            ? ((documentsData as Document)[docData] || [])
                .filter((doc) => {
                  if (
                    hasPOA &&
                    applicant.applicant_type !== '1' &&
                    doc.documentType === 'poaNotarized'
                  ) {
                    return;
                  }
                  if (
                    !applicant.correspondenceAddressEdited &&
                    doc.documentType === 'correspondenceAddressProof'
                  ) {
                    return;
                  }
                  return doc;
                })
                .filter((ele) => ele)
                // .map((document) => {
                //   if (
                //     document.documentType === 'screeningReport' &&
                //     role === USER_ROLES.AMCAPPROVER
                //   ) {
                //     return { ...document, required: 'true' };
                //   }
                //   return document;
                // })
                .map((document) => {
                  if (
                    ['investorSignature', 'signature_of_investor'].includes(
                      document.documentType
                    ) &&
                    checkValidationBasedOnDate(
                      application?.createdAt,
                      fewFieldsInKYCDetailsAndInvestorSignatureDocumentValidationDate
                    )
                  ) {
                    return { ...document, required: 'true' };
                  }
                  if (
                    applicant.correspondenceAddressEdited &&
                    document.documentType === 'correspondenceAddressProof'
                  ) {
                    return { ...document, required: 'true' };
                  }
                  return document;
                })
                .filter((_doc) => {
                  if (_doc.documentType === 'screeningReport' && role !== USER_ROLES.AMCAPPROVER) {
                    return;
                  }
                  return _doc;
                })
                .filter((ele) => ele)
                .filter((document) => {
                  if (applicant.amlCheck && document.documentType === 'compliance_document') {
                    return;
                  }
                  return document;
                })
                .filter((ele) => ele)
                .map((document) => {
                  if (
                    (document.documentType === 'relationship' &&
                      applicant.applicant_type !== '1') ||
                    (document.documentType === 'distributorSignature' &&
                      applicant.applicant_type === '1')
                  ) {
                    return { ...document, required: 'true' };
                  }
                  return document;
                })
                .filter((document) => {
                  if (
                    (document.documentType === 'relationship' &&
                      applicant.applicant_type === '1') ||
                    (document.documentType === 'distributorSignature' &&
                      applicant.applicant_type !== '1')
                  ) {
                    return;
                  }
                  return document;
                })
                .filter((ele) => ele)
                .map((document) => {
                  if (applicant.fatcaCrsDocumentCheck && document.documentType === 'fatca') {
                    return { ...document, required: 'true' };
                  }
                  return document;
                })
                .map((_document) => {
                  if (
                    _document.documentType === 'sourceOfFund' &&
                    ['HOUSEWIFE'].includes(applicant?.occupationDetails?.toUpperCase() || '')
                  ) {
                    return { ..._document, required: 'true' };
                  }
                  if (
                    ['declarationFromInvestors'].includes(_document.documentType) &&
                    ['HOUSEWIFE', 'STUDENT', 'RETIRED', 'SENIOR CITIZEN'].includes(
                      applicant?.occupationDetails?.toUpperCase() || ''
                    )
                  ) {
                    return { ..._document, required: 'true' };
                  }
                  return _document;
                })
                .map((_document) => {
                  if (
                    ['sourceOfFund', 'declarationFromInvestors'].includes(_document.documentType) &&
                    applicantStatusMasters[applicant.status as string] !==
                      applicantStatusMasters.NRI &&
                    applicant.politicallyExposedPersonStatus !== 'Not Applicable'
                  ) {
                    return { ..._document, required: 'true' };
                  }
                  return _document;
                })
                .filter((_doc) => {
                  if (
                    cleardpIdField(applicant?.dlclId || '') &&
                    _doc.documentType === 'demat_information'
                  ) {
                    return;
                  }
                  return _doc;
                })
                .filter((ele) => ele)
                .map((_doc) => {
                  if (
                    applicant?.dlclId &&
                    !cleardpIdField(applicant?.dlclId || '') &&
                    _doc.documentType === 'demat_information'
                  ) {
                    return { ..._doc, required: 'true' };
                  }
                  return _doc;
                })
                .map((doc: individuals_Poa_nonIndividuals_Documents, index: number) => {
                  const {
                    documentType,
                    documentName,
                    multipleFiles,
                    required,
                    options,
                    downloadTemplate,
                    infoText,
                  } = doc;
                  const { documents: existingDocuments = [] } = applicant || {};
                  const docsOfCurrentDocType = existingDocuments
                    .filter((doc) => doc.documentType === documentType)
                    .sort((doc1, doc2) => Number(doc1.documentId) - Number(doc2.documentId));
                  return {
                    documentType: documentType,
                    documentName: documentName,
                    documentsList: docsOfCurrentDocType.length
                      ? docsOfCurrentDocType.map((doc, ind) => {
                          const {
                            documentName = '',
                            documentType = '',
                            documentId = '',
                            isActive = true,
                            file = undefined,
                          } = doc;
                          return {
                            documentType,
                            documentName,
                            documentId,
                            isActive,
                            file,
                            options,
                            required,
                            isVisible: true,
                            infoText,
                            downloadTemplate,
                            uniqueKey: (applicant_index.toString() +
                              '-' +
                              index.toString() +
                              '-' +
                              ind.toString()) as string,
                          };
                        })
                      : [
                          {
                            documentType,
                            documentName,
                            required,
                            options,
                            downloadTemplate,
                            infoText,
                            isVisible: true,
                            uniqueKey: (applicant_index.toString() +
                              '-' +
                              index.toString() +
                              '-' +
                              '0') as string,
                            ...document_object,
                          },
                        ],
                    required,
                    multipleFiles,
                    options,
                    downloadTemplate,
                    infoText,
                  };
                })
            : [],
        kraDocument: applicant.kraDocument,
        occupationDetails: applicant?.occupationDetails || '',
      };
    }),
    nominees: nominees.map((nominee, nominee_index) => {
      return {
        nomineedocuments:
          typeof documentsData !== 'undefined'
            ? (isMinor(nominee.dateOfBirth || '')
                ? [
                    ...((documentsData as Document)[APPLICATION_TYPE_FOR_DOCUMENTS.NOMINEE_DOC] ||
                      []),
                    ...((documentsData as Document)[
                      APPLICATION_TYPE_FOR_DOCUMENTS.NOMINEE_GUARDIAN
                    ] || []),
                  ].map((doc) => {
                    if (doc.documentType === 'nomineeIdProof') {
                      return { ...doc, required: 'false' };
                    }
                    return doc;
                  })
                : (documentsData as Document)[APPLICATION_TYPE_FOR_DOCUMENTS.NOMINEE_DOC] || []
              )
                .filter((document) => {
                  if (!hasPOA && document.documentType === 'nomination_form') {
                    return;
                  }
                  return document;
                })
                .filter((ele) => ele)
                .map((document) => {
                  if (
                    nominee.nominationDocumentCheck &&
                    document.documentType === 'nomination_form'
                  ) {
                    return { ...document, required: 'true' };
                  }
                  return document;
                })
                .map((doc: individuals_Poa_nonIndividuals_Documents, index: number) => {
                  const { documentType, documentName, multipleFiles, required, options } = doc;
                  const { nomineedocuments: existingDocuments = [] } = nominee || {};
                  const docsOfCurrentDocType = existingDocuments
                    .filter((doc) => doc.documentType === documentType)
                    .sort((doc1, doc2) => Number(doc1.documentId) - Number(doc2.documentId));
                  return {
                    documentType: documentType,
                    documentName: documentName,
                    documentsList: docsOfCurrentDocType.length
                      ? docsOfCurrentDocType.map((doc, ind) => {
                          const {
                            documentName = '',
                            documentType = '',
                            documentId = '',
                            isActive = true,
                            file = undefined,
                          } = doc;
                          return {
                            documentType,
                            documentName,
                            documentId,
                            isActive,
                            file,
                            options,
                            required,
                            isVisible: true,
                            uniqueKey: (nominee_index.toString() +
                              '-' +
                              index.toString() +
                              '-' +
                              ind.toString()) as string,
                          };
                        })
                      : [
                          {
                            documentType,
                            documentName,
                            required,
                            options,
                            isVisible: true,
                            uniqueKey: (nominee_index.toString() +
                              '-' +
                              index.toString() +
                              '-' +
                              '0') as string,
                            ...document_object,
                          },
                        ],
                    required,
                    multipleFiles,
                    options,
                  };
                })
            : [],
      };
    }),

    saveType: 'save and proceed',
    banks: banks,
    hasPOA: hasPOA,
  };
  const updatedApplicants = (
    exisitingApplicants: Partial<Applicant>[],
    applicants: documentProps[]
  ): Partial<Applicant>[] => {
    return exisitingApplicants.map((applicant, index) => {
      const { documents = [] } = applicants[index] || {};
      const { documents: existDocuments = [] } = applicant || {};
      const addScreeningReport = existDocuments
        .map((_doc) => {
          if (_doc.documentType === 'screeningReport') {
            const {
              documentData,
              id,
              applicantId,
              filename,
              updatedAt,
              createdAt,
              updatedBy,
              createdBy,
              ...rest
            } = _doc;
            return { ...rest };
          }
          return;
        })
        .filter((ele) => ele);
      const finalDocs = documents
        .map((doc) => {
          return doc.documentsList.filter((d) => d.documentId);
        })
        .flat()
        .map((doc) => {
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          const { uniqueKey, required, ...rest } = doc;
          return rest;
        });
      const includingScreeningReport =
        role === USER_ROLES.AMCAPPROVER ? finalDocs : [...finalDocs, ...addScreeningReport];
      return {
        ...applicant,
        documents: includingScreeningReport,
      };
    }) as Partial<Applicant>[];
  };

  const updatedNominees = (
    existingNominees: NomineeType[],
    nominees: nomineeDocumentProps[]
  ): NomineeType[] => {
    return existingNominees.map((nominee, index) => {
      const { nomineedocuments = [] } = nominees[index] || {};
      const finalDocs = nomineedocuments.map((doc) => {
        return doc.documentsList.filter((d) => d.documentId);
      });
      return {
        ...nominee,
        nomineedocuments: finalDocs.flat().map((doc) => {
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          const { uniqueKey, required, ...rest } = doc;
          return rest;
        }),
      };
    }) as NomineeType[];
  };

  const handleSubmit = async (values: Values) => {
    try {
      const nationalitiesMdmsMasters = (await dispatch(
        getNationalityList()
      )) as unknown as nationaliyType;
      await isFormValidForSubmission(application, false, true, nationalitiesMdmsMasters);
      const {
        applicants: exisitingApplicants = [],
        id,
        applicant1ReferenceId,
        currentStep,
        status,
        applicationNumber,
        nominees: existingNominees = [],
      } = application || {};
      const { applicants, saveType, hasPOA, nominees } = values;

      // const updatedApplicants = exisitingApplicants.map((applicant, index) => {
      //   const applicantsWithNoDocsAttached = Object.keys(applicants[index].documents).filter(
      //     (doc) => applicants[index].documents[doc].documentId
      //   );
      //   return {
      //     ...applicant,
      //     countryCode: '+91', //For now hard coding country code. Need to remove after UI is done
      //     documents: applicantsWithNoDocsAttached.map((documentType) => ({
      //       ...getExistingDocuments(exisitingApplicants[index].documents || [], documentType),
      //       ...applicants[index].documents[documentType],
      //     })),
      //   };
      // });

      const updatedApplicantsWithDoc = updatedApplicants(exisitingApplicants, applicants);
      updatedApplicantsWithDoc.map((applicant) => {
        if (applicant.documents?.length) {
          applicant.documents.map((document) => {
            if (document.options && !document.options.includes(document.documentName)) {
              throw `Please Select ${getFieldTitle[document.documentType]}`;
            }
          });
        }
      });
      const updatedNomineesWithDoc = updatedNominees(existingNominees, nominees);
      updatedNomineesWithDoc.map((_nominee) => {
        if (_nominee.nomineedocuments?.length) {
          _nominee.nomineedocuments.map((document) => {
            if (document.options && !document.options.includes(document.documentName)) {
              throw `Please Select ${getFieldTitle[document.documentType]}`;
            }
          });
        }
      });
      const isSaveLater = saveType !== 'save and proceed';
      const checkApplication = applicationComparison(
        {
          ...application,
          applicants: application?.applicants
            ?.map((applicant) => {
              return {
                ...applicant,
                documents: applicant.documents
                  ?.map((document: any) => {
                    const {
                      documentData,
                      id,
                      applicantId,
                      filename,
                      updatedAt,
                      createdAt,
                      updatedBy,
                      createdBy,
                      ...rest
                    } = document;
                    return { ...rest };
                  })
                  .sort((doc1, doc2) => Number(doc1.documentId) - Number(doc2.documentId)),
              };
            })
            .sort((applicant1, applicant2) => Number(applicant1.id) - Number(applicant2.id)),
          nominees: application?.nominees
            ?.map((nominee) => {
              return {
                ...nominee,
                nomineedocuments: nominee.nomineedocuments
                  ?.map((document: any) => {
                    const {
                      documentData,
                      id,
                      nomineeId,
                      filename,
                      updatedAt,
                      createdAt,
                      updatedBy,
                      createdBy,
                      ...rest
                    } = document;
                    return { ...rest };
                  })
                  .sort((doc1, doc2) => Number(doc1.documentId) - Number(doc2.documentId)),
              };
            })
            .sort((nominee1, nominee2) => Number(nominee1.id) - Number(nominee2.id)),
        },
        {
          ...application,
          applicants: updatedApplicantsWithDoc
            .map((applicant) => {
              return {
                ...applicant,
                documents: applicant.documents
                  ?.map((document: any) => {
                    const { documentData, options, infoText, downloadTemplate, ...rest } = document;
                    return { ...rest };
                  })
                  .sort((doc1, doc2) => Number(doc1.documentId) - Number(doc2.documentId)),
              };
            })
            .sort((applicant1, applicant2) => Number(applicant1.id) - Number(applicant2.id)),
          nominees: updatedNomineesWithDoc
            .map((nominee) => {
              return {
                ...nominee,
                nomineedocuments: nominee.nomineedocuments
                  ?.map((document: any) => {
                    const { documentData, options, ...rest } = document;
                    return { ...rest };
                  })
                  .sort((doc1, doc2) => Number(doc1.documentId) - Number(doc2.documentId)),
              };
            })
            .sort((nominee1, nominee2) => Number(nominee1.id) - Number(nominee2.id)),
          currentStep:
            !!currentStep &&
            currentStep > getCurrentStep(application?.applicationType || '', path, isSaveLater) - 1
              ? currentStep
              : getCurrentStep(application?.applicationType || '', path, isSaveLater),
        }
      );
      if (id && !checkApplication) {
        setLoading(true);
        await dispatch(
          updateApplication({
            body: {
              ...application,
              applicants: updatedApplicantsWithDoc,
              nominees: updatedNomineesWithDoc,
              // : (status === 'draft' || status === 'sent_to_amc_approver') &&
              status:
                !application?.hasPOA &&
                AMC_APPROVER_CHECK_FOR_INDIVIDUAL &&
                applyRoleBasedStatus(role) &&
                status !== 'draft'
                  ? 'sent_to_amc_approver'
                  : status === 'sent_to_amc_approver' &&
                    hasPOA &&
                    USER_ROLES.SUBDISTRIBUTOR === role
                  ? 'draft'
                  : status,
              currentStep: getCurrentStep(application?.applicationType || '', path, isSaveLater),
              ...((!SAVE_LATER_VALIDATION_CHECK as boolean) && { saveForLater: isSaveLater }),
            },
            applicationId: id,
            ...(isSaveLater && {
              toastMessage: '',
            }),
          })
        );
      } else if (isSaveLater) {
        enqueueSnackbar(`Application ${applicationNumber} - ` + ' Saved successfully', {
          variant: 'success',
          autoHideDuration: 3000,
        });
      }
      history.push(
        navigateToNextStep(
          path,
          role,
          id || '',
          isSaveLater,
          applicant1ReferenceId || '',
          application?.applicationType || ''
        ),
        { id, applicant1ReferenceId }
      );
    } catch (e) {
      setLoading(false);
      typeof e === 'string' && dispatch(showError(e));
      console.error((e as Error).message);
    }
  };

  const isFieldDisabled = [USER_ROLES.INVESTOR].includes(role);
  const getLabel = (
    documentName: string,
    documentType: string,
    required: string,
    values: Values,
    ind: number
  ): string => {
    const isNRI =
      applicantStatusMasters[application?.applicants?.[ind]?.status as string] ===
      applicantStatusMasters.NRI;
    const label = isNRI && documentType === 'form_10ff_trc' ? 'Form 41' : documentName;
    return `${label}  ${required === 'true' ? '*' : ''} `;
  };

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={handleSubmit}
      //validationSchema={documentDetailsSchema}
      validate={(values: Values) => {
        if (
          (values.saveType === 'save for later' && SAVE_LATER_VALIDATION_CHECK) ||
          values.saveType !== 'save for later'
        ) {
          try {
            validateYupSchema(values, documentDetailsSchema, true, values);
          } catch (e) {
            return yupToFormErrors(e);
          }
        }
      }}
      enableReinitialize={true}>
      {({
        handleSubmit,
        values,
        setValues,
        validateForm,
        setTouched,
        setErrors,
        setFieldValue,
      }) => (
        <Grid
          container
          rowSpacing={1}
          columnSpacing={10}
          component="form"
          noValidate
          onSubmit={handleSubmit}
          sx={{
            width: '100%',
            ml: 0,
            '.MuiGrid-item': { px: { xs: 0, sm: '30px' } },
          }}>
          <Grid sx={{ width: '100%' }}>
            <TwoNotes
              displayContent1={'Please upload latest documents not more than 2 months old.'}
              displayContent2={`Documents needs to be self-certified or duly certified by an authorised person.`}
              displayContent3={`Please don't upload any password protected files.`}
            />
          </Grid>
          {values.applicants.map((applicant, ind) => {
            const { documents } = applicant;
            return (
              <>
                {' '}
                <SubHeading>Applicant {ind + 1} Document Details</SubHeading>
                {groupDocumentsBySection(documents).map((section) => (
                  <React.Fragment key={`${ind}-${section.heading}`}>
                    <SubHeading>{section.heading}</SubHeading>
                    {section.items.map(({ document, doc_index }) => {
                      const {
                        documentType,
                        documentName,
                        documentsList = [],
                        multipleFiles,
                        options,
                        required,
                        infoText,
                        downloadTemplate,
                      } = document;
                      return (
                        <React.Fragment key={`${ind}-${doc_index}`}>
                          <Grid item xs={12} sm={6}>
                            {options &&
                              documentsList
                                .filter(
                                  (document, index) =>
                                    documentsList.findIndex(
                                      (obj) => document.documentType === obj.documentType
                                    ) === index
                                )
                                .map((doc, index) => (
                                  <React.Fragment key={index}>
                                    <Grid xs={12} sm={12}>
                                      <MFSelectField
                                        name={`applicants.${ind}.documents.${doc_index}.documentsList.${index}.documentName`}
                                        label={getLabel(
                                          documentName,
                                          documentType,
                                          required,
                                          values,
                                          ind
                                        )}
                                        items={options.map((item: string) => ({
                                          key: item,
                                          value: item,
                                        }))}
                                        disabled={isFieldDisabled || false}
                                        applyLabelStyles={true}
                                        onChange={({
                                          target: { value },
                                        }: SelectChangeEvent<unknown>) => {
                                          const updatedDocumentList = values.applicants[
                                            ind
                                          ].documents[doc_index].documentsList.map((docList) => {
                                            if (docList.documentType === doc.documentType) {
                                              return {
                                                ...docList,
                                                documentName: value,
                                              };
                                            }
                                            return docList;
                                          });
                                          setFieldValue(
                                            `applicants.${ind}.documents.${doc_index}.documentsList`,
                                            updatedDocumentList
                                          );
                                        }}
                                      />
                                    </Grid>
                                  </React.Fragment>
                                ))}
                            {!options && infoText && (
                              <Typography
                                sx={{
                                  color: 'primary.main',
                                  fontWeight: 500,
                                  mt: 6,
                                }}>
                                {getLabel(documentName, documentType, required, values, ind)}
                                <Typography component="span" pl={0.5} sx={{ cursor: 'pointer' }}>
                                  <InfoOutlinedIcon
                                    color="info"
                                    fontSize="small"
                                    onClick={() => {
                                      setPopUpText({ popUpInfoText: infoText });
                                      handleOpen();
                                    }}
                                  />
                                </Typography>
                              </Typography>
                            )}
                            {!options && !infoText && (
                              <Typography
                                sx={{
                                  color: 'primary.main',
                                  fontWeight: 500,
                                  mt: documentType === 'investorSignature' ? 4 : 6,
                                }}>
                                {getLabel(documentName, documentType, required, values, ind)}
                                {documentType === 'investorSignature' && (
                                  <Typography
                                    sx={{
                                      color: 'primary.main',
                                      fontWeight: 400,
                                      fontSize: 14,
                                    }}>
                                    (This signature copy is used as a verification process for
                                    future redemptions and transfer requests)
                                  </Typography>
                                )}
                              </Typography>
                            )}
                          </Grid>
                          <Grid
                            item
                            xs={12}
                            sm={6}
                            display="flex"
                            flexDirection="column"
                            mt={options ? { xs: 1, sm: 0 } : 0}>
                            {documentsList
                              .filter((item) => item.isVisible)
                              .map((doc, index) => (
                                <React.Fragment key={doc.uniqueKey}>
                                  <FileUpload
                                    onFileChange={(value, documentId, removeFile) => {
                                      const { file, ...rest } =
                                        values.applicants[ind].documents[doc_index].documentsList[
                                          Number(doc.uniqueKey.split('-')[2])
                                        ];
                                      setFieldValue(
                                        `applicants.${ind}.documents.${doc_index}.documentsList.${
                                          doc.uniqueKey.split('-')[2]
                                        }`,
                                        {
                                          ...rest,
                                          documentId,
                                          ...(!removeFile && { file }),
                                        }
                                      );
                                    }}
                                    name={`applicants.${ind}.documents.${doc_index}.documentsList.${
                                      doc.uniqueKey.split('-')[2]
                                    }.documentId`}
                                    documentData={doc}
                                    disabled={
                                      isFieldDisabled ||
                                      // Disable demat_information upload when fetched from CML and already uploaded
                                      Boolean(
                                        documentType === 'demat_information' &&
                                          application?.applicants?.[ind]?.isCMLFetched &&
                                          doc.documentId
                                      )
                                    }
                                    onFileUpload={handleUpload}
                                    onFileUploaded={handleUploaded}
                                  />
                                  {index > 0 && doc.isVisible && !doc.documentId && (
                                    <DeleteOutlineIcon
                                      onClick={() => {
                                        const getDocs = values.applicants[ind].documents[
                                          doc_index
                                        ].documentsList
                                          // .filter((_, i) => i !== index)
                                          .map((_item, i) => ({
                                            ..._item,
                                            isVisible:
                                              i !== Number(doc.uniqueKey.split('-')[2])
                                                ? _item.isVisible
                                                : false,
                                          }));
                                        setFieldValue(
                                          `applicants.${ind}.documents.${doc_index}.documentsList`,
                                          getDocs
                                        );
                                      }}
                                      color="error"
                                      sx={{
                                        alignSelf: 'end',
                                        position: 'relative',
                                        bottom: '90px',
                                        left: '22px',
                                        cursor: 'pointer',
                                      }}
                                    />
                                  )}
                                </React.Fragment>
                              ))}
                            {multipleFiles === 'true' &&
                              // isFieldDisabled &&
                              documentsList.filter((item) => item.isVisible).length <
                                (documentType === 'additional' ? 2 : 2) && (
                                <Typography
                                  onClick={() => {
                                    const currentDocTypeListLength =
                                      values.applicants[ind].documents[doc_index].documentsList
                                        .length;
                                    const {
                                      required = true,
                                      documentType,
                                      documentName,
                                      // eslint-disable-next-line
                                      documentId,
                                    } = values.applicants[ind].documents[doc_index].documentsList[
                                      currentDocTypeListLength - 1
                                    ];
                                    try {
                                      documentsList
                                        .filter((item) => item.isVisible)
                                        .forEach((item, _i) => {
                                          if (!item.documentId) {
                                            throw Error(
                                              `Please upload the ${getDocNumberForMultipleDocs(
                                                _i + 1
                                              )}  document to add more documents`
                                            );
                                          }
                                        });
                                      setFieldValue(
                                        `applicants.${ind}.documents.${doc_index}.documentsList`,
                                        [
                                          ...values.applicants[ind].documents[doc_index]
                                            .documentsList,
                                          {
                                            ...document_object,
                                            isVisible: true,
                                            uniqueKey:
                                              ind.toString() +
                                              '-' +
                                              doc_index.toString() +
                                              '-' +
                                              currentDocTypeListLength.toString(),
                                            documentType,
                                            documentName,
                                            required,
                                          },
                                        ]
                                      );
                                    } catch (e) {
                                      dispatch(showError((e as Error).message));
                                    }
                                  }}
                                  sx={{
                                    color: '#417850',
                                    fontSize: '12px',
                                    fontWeight: 500,
                                    textDecoration: 'underline',
                                    alignSelf: 'end',
                                    cursor: 'pointer',
                                  }}>
                                  {'Add another document'}
                                </Typography>
                              )}
                          </Grid>
                        </React.Fragment>
                      );
                    })}
                  </React.Fragment>
                ))}
                {ENABLE_KRA_DOCUMENT_FETCH && USER_ROLES.INVESTOR !== role && (
                  <>
                    <SubHeading>KRA Documents</SubHeading>
                    <KraDocument KraDocument={applicant.kraDocument} />
                  </>
                )}
              </>
            );
          })}
          {values.nominees.map((nominee, ind) => {
            const { nomineedocuments } = nominee;
            return (
              <>
                {' '}
                <SubHeading> {getApplicantName(ind + 1, true)} Nominee Document Details</SubHeading>
                {nomineedocuments.map((document, doc_index) => {
                  const {
                    documentType,
                    documentName,
                    documentsList = [],
                    multipleFiles,
                    options,
                    required,
                  } = document;
                  return (
                    <React.Fragment key={ind}>
                      <Grid item xs={12} sm={6}>
                        {options &&
                          documentsList
                            .filter(
                              (document, index) =>
                                documentsList.findIndex(
                                  (obj) => document.documentType === obj.documentType
                                ) === index
                            )
                            .filter((item) => item.isVisible)
                            .map((doc, index) => (
                              <React.Fragment key={index}>
                                <Grid xs={12} sm={12}>
                                  <MFSelectField
                                    name={`nominees.${ind}.nomineedocuments.${doc_index}.documentsList.${index}.documentName`}
                                    label={getLabel(
                                      documentName,
                                      documentType,
                                      required,
                                      values,
                                      ind
                                    )}
                                    items={options.map((item: string) => ({
                                      key: item,
                                      value: item,
                                    }))}
                                    disabled={isFieldDisabled || false}
                                    applyLabelStyles={true}
                                    onChange={({
                                      target: { value },
                                    }: SelectChangeEvent<unknown>) => {
                                      const updatedDocumentList = values.nominees[
                                        ind
                                      ].nomineedocuments[doc_index].documentsList.map((docList) => {
                                        if (docList.documentType === doc.documentType) {
                                          return {
                                            ...docList,
                                            documentName: value,
                                          };
                                        }
                                        return docList;
                                      });
                                      setFieldValue(
                                        `nominees.${ind}.nomineedocuments.${doc_index}.documentsList`,
                                        updatedDocumentList
                                      );
                                    }}
                                  />
                                </Grid>
                              </React.Fragment>
                            ))}
                        {!options && (
                          <Typography
                            sx={{
                              color: 'primary.main',
                              fontWeight: 500,
                              mt: documentType === 'investorSignature' ? 4 : 6,
                            }}>
                            {documentName} {required === 'true' ? '*' : ''}
                          </Typography>
                        )}
                      </Grid>
                      <Grid
                        item
                        xs={12}
                        sm={6}
                        display="flex"
                        flexDirection="column"
                        mt={options ? { xs: 1, sm: 0 } : 0}>
                        {documentsList.map((doc, index) => (
                          <React.Fragment key={doc.uniqueKey}>
                            <FileUpload
                              onFileChange={(value, documentId, removeFile) => {
                                const { file, ...rest } =
                                  values.nominees[ind].nomineedocuments[doc_index].documentsList[
                                    index
                                  ];
                                setFieldValue(
                                  `nominees.${ind}.nomineedocuments.${doc_index}.documentsList.${index}`,
                                  {
                                    ...rest,
                                    documentId,
                                    ...(!removeFile && { file }),
                                  }
                                );
                              }}
                              name={`nominees.${ind}.nomineedocuments.${doc_index}.documentsList.${index}.documentId`}
                              documentData={doc}
                              disabled={isFieldDisabled}
                              onFileUpload={handleUpload}
                              onFileUploaded={handleUploaded}
                            />
                          </React.Fragment>
                        ))}
                        {multipleFiles === 'true' &&
                          isFieldDisabled &&
                          documentsList.filter((item) => item.isVisible).length < 2 && (
                            <Typography
                              onClick={() => {
                                const currentDocTypeListLength =
                                  values.nominees[ind].nomineedocuments[doc_index].documentsList
                                    .length;
                                const {
                                  required = true,
                                  documentType,
                                  documentName,
                                  documentId,
                                } = values.nominees[ind].nomineedocuments[doc_index].documentsList[
                                  currentDocTypeListLength - 1
                                ];
                                try {
                                  if (!documentId) {
                                    throw Error(
                                      'Please upload the first document to add more documents'
                                    );
                                  }
                                  setFieldValue(
                                    `nominees.${ind}.nomineedocuments.${doc_index}.documentsList`,
                                    [
                                      ...values.nominees[ind].nomineedocuments[doc_index]
                                        .documentsList,
                                      {
                                        ...document_object,
                                        uniqueKey:
                                          ind.toString() +
                                          '-' +
                                          doc_index.toString() +
                                          '-' +
                                          currentDocTypeListLength.toString(),
                                        documentType,
                                        documentName,
                                        required,
                                      },
                                    ]
                                  );
                                } catch (e) {
                                  dispatch(showError((e as Error).message));
                                }
                              }}
                              sx={{
                                color: '#417850',
                                fontSize: '12px',
                                fontWeight: 500,
                                textDecoration: 'underline',
                                alignSelf: 'end',
                                cursor: 'pointer',
                              }}>
                              {'Add another document'}
                            </Typography>
                          )}
                      </Grid>
                    </React.Fragment>
                  );
                })}
              </>
            );
          })}
          {!SAVE_LATER_VALIDATION_CHECK && (
            <Grid sx={{ width: '100%', mt: 4 }}>
              <Notes displayContent={'Mandatory Field will be Verified on Clicking Preview'} />
            </Grid>
          )}
          <ProceedSaveLater
            saveButtonText={
              isFieldDisabled || USER_ROLES.AMCAPPROVER === role ? 'Save Application' : 'Preview'
            }
            // saveButtonText={
            //   isFieldDisabled || USER_ROLES.AMCAPPROVER === role
            //     ? 'Save Application'
            //     : values.hasPOA
            //     ? 'Send to POA Approver'
            //     : // : (application?.status === 'draft' ||
            //     //     application?.status === 'sent_to_amc_approver') &&
            //     AMC_APPROVER_CHECK_FOR_INDIVIDUAL && applyRoleBasedStatus(role)
            //     ? 'Send to AIF Approver'
            //     : 'Send to Investor'
            // }
            showEndIcon={false}
            saveLater={() => {
              setFieldValue('saveType', 'save for later');
            }}
            // saveAndProceed={async () => {
            //   try {
            //     if (![USER_ROLES.INVESTOR, USER_ROLES.POAAPPROVER].includes(role)) {
            //       await isFormValidForSubmission(application, false);
            //       const error = await validateForm();
            //       if (!Object.keys(error).length) {
            //         history.push(`/application-details/${application?.id}`);
            //         //setDialog(true);
            //       }
            //       setTouched(setNestedObjectValues(error, true));
            //       setErrors(error);
            //     }
            //   } catch (e) {
            //     dispatch(showError(e as string));
            //   }
            // }}
            //saveAndProceedType={isFieldDisabled ? 'submit' : 'button'}
            loader={loading}
            clickedButton={values.saveType}
            showSaveForLater={
              isFieldDisabled ? false : USER_ROLES.AMCAPPROVER === role ? false : true
            }
            disabled={documentUpload}
            saveAndProceed={() => setFieldValue('saveType', 'save and proceed')}
          />
          <Dialog
            onClose={handleClose}
            open={open}
            sx={{
              '.MuiPaper-root ': {
                maxWidth: 800,
                p: { xs: 2, sm: '15px 5px 35px' },
                borderRadius: '10px',
                height: 'unset',
                overflowY: 'auto',
              },
            }}>
            <IconButton onClick={handleClose} sx={{ alignSelf: 'flex-end' }}>
              <CloseIcon fontSize="medium" />
            </IconButton>
            <Box
              sx={{
                width: { xs: '100%', sm: '90%' },
                mx: 'auto',
                '.MuiTypography-root': {
                  textAlign: 'start',
                  fontFamily: 'Work Sans,sans-serif',
                },
              }}>
              {popUpText?.popUpInfoText?.map((info: string, infoIndex: number) => {
                return (
                  <Typography sx={{ fontSize: 16, fontWeight: 400, pt: 1 }} key={infoIndex}>
                    <Typography sx={{ fontSize: 16, fontWeight: 500 }} component="span">
                      {infoIndex + 1} .{' '}
                    </Typography>
                    {info}
                  </Typography>
                );
              })}
              <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                <Button
                  variant="contained"
                  sx={{ minWidth: { xs: '170px', sm: '220px' }, mt: 3 }}
                  onClick={handleClose}>
                  Done
                </Button>
              </Box>
            </Box>
          </Dialog>
        </Grid>
      )}
    </Formik>
  );
}

export const DownloadTemplate = ({ url, disabled }: { url: string; disabled: boolean }) => {
  return (
    <Box sx={{ textAlign: 'center' }}>
      <Button
        variant="contained"
        sx={{
          color: 'common.white',
          minWidth: '100%',
          fontWeight: 600,
          lineHeight: 1,
          mt: { xs: 2 },
          fontSize: '12px',
        }}
        disabled={disabled}
        onClick={async () => {
          window.open(`https://iciciventureaif.kfintech.com/api/mdms/${url}`, '_blank');
        }}>
        Download template
      </Button>
    </Box>
  );
};
